import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

const LIVE_MONTAGE =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_irwan-craw-music/artifacts/uxq18yhk_ChatGPT%20Image%2030%20juil.%202026%2C%2020_29_47.png";

// Deterministic gold particles
const PARTICLES = [
  { x: 6, y: 20, s: 1.6, d: 0 },
  { x: 22, y: 76, s: 1.4, d: 2.4 },
  { x: 44, y: 30, s: 2, d: 1.1 },
  { x: 62, y: 66, s: 1.6, d: 3.5 },
  { x: 82, y: 24, s: 2, d: 4.2 },
  { x: 94, y: 82, s: 1.4, d: 1.8 },
];

const VIBES = [
  { title: "Sunset Sets", text: "Beach clubs · rooftops · open-air stages." },
  { title: "Panama Signature", text: "Linen, gold accents, endless summer." },
  { title: "Latin Grooves", text: "From Ibiza to Tulum, one continuous journey." },
  { title: "Crowd Ritual", text: "Hands in the air the moment the sun drops." },
];

export const LiveUniverse = () => {
  return (
    <section
      id="live"
      data-testid="live-section"
      className="relative w-full bg-bg py-24 md:py-32 border-t border-[color:var(--line)] overflow-hidden"
    >
      {/* Ambient gold glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 60% 40% at 10% 15%, rgba(201,169,97,0.10), transparent 60%), radial-gradient(ellipse 60% 40% at 90% 90%, rgba(201,169,97,0.08), transparent 60%)",
        }}
      />

      {/* Floating gold particles */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {PARTICLES.map((p, i) => (
          <motion.span
            key={i}
            aria-hidden
            initial={{ opacity: 0, y: 0 }}
            animate={{
              opacity: [0, 0.7, 0.3, 0.7, 0],
              y: [0, -30, -60, -100, -140],
            }}
            transition={{
              duration: 15 + (i % 4) * 2,
              delay: p.d,
              ease: "linear",
              repeat: Infinity,
            }}
            className="absolute rounded-full"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: `${p.s}px`,
              height: `${p.s}px`,
              background:
                "radial-gradient(circle, rgba(229,204,148,0.9) 0%, rgba(201,169,97,0.4) 60%, rgba(201,169,97,0) 100%)",
              boxShadow: "0 0 6px rgba(229,204,148,0.6)",
            }}
          />
        ))}
      </div>

      <div className="relative mx-auto max-w-[1500px] px-6 md:px-10">
        {/* Header */}
        <motion.div
          variants={stagger(0.1, 0.05)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-12 md:mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <motion.div variants={fadeUp} className="max-w-2xl">
            <p className="mb-3 inline-flex items-center gap-3 text-[10px] font-semibold tracking-[0.32em] uppercase text-gold">
              <span className="h-px w-8 bg-[color:var(--gold)]/70" />
              Chapter 05 — Live
            </p>
            <h2
              data-testid="live-title"
              className="font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-ink"
            >
              The Live{" "}
              <span className="font-editorial text-gold-hi">experience</span>
            </h2>
          </motion.div>
          <motion.p
            variants={fadeUp}
            className="max-w-md text-sm text-muted leading-relaxed"
          >
            From golden sunsets to unforgettable nights, Irwan Craw creates
            immersive soundtracks for beach clubs, clubs and open-air stages
            around the world.
          </motion.p>
        </motion.div>

        {/* Editorial montage frame */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="relative"
        >
          <div className="pointer-events-none absolute -inset-3 rounded-[2px] border border-[color:var(--line)]" />
          <div className="pointer-events-none absolute -inset-6 rounded-[4px] border border-[color:var(--line)]/60" />

          <div className="group relative overflow-hidden rounded-sm border border-[color:var(--gold)]/30 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)]">
            <img
              src={LIVE_MONTAGE}
              alt="Irwan Craw performing at sunset — Scorpios, Mykonos live atmosphere"
              className="block w-full h-auto transition-transform duration-[1400ms] ease-out group-hover:scale-[1.025]"
              loading="lazy"
              data-testid="live-montage-image"
            />

            {/* Warm overlay */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(10,10,10,0) 40%, rgba(10,10,10,0.35) 72%, rgba(10,10,10,0.82) 100%)",
              }}
            />
            {/* Deeper darken on bottom-right for signature readability */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse 55% 40% at 82% 96%, rgba(10,10,10,0.72), transparent 70%)",
              }}
            />
            {/* Signature caption */}
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-8 flex items-end justify-between">
              <div>
                <p className="text-[9px] md:text-[10px] font-semibold uppercase tracking-[0.32em] text-gold">
                  <span className="text-[color:var(--gold-hi)]">Live</span> ·
                  Sunset · Beach Club
                </p>
                <p className="mt-1 font-editorial text-lg md:text-2xl text-ink/95">
                  Endless summer, endless groove.
                </p>
              </div>
              <span className="hidden md:inline font-serif-signature text-2xl text-[color:var(--gold-hi)]/90">
                Irwan Craw
              </span>
            </div>
          </div>
        </motion.div>

        {/* Vibe cards */}
        <motion.div
          variants={stagger(0.1, 0.1)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mt-8 md:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5"
        >
          {VIBES.map((v, i) => (
            <motion.div
              key={v.title}
              variants={fadeUp}
              data-testid={`live-vibe-${i}`}
              className="group relative overflow-hidden rounded-sm border border-[color:var(--line)] bg-bg-2/60 backdrop-blur-md p-6 transition-all duration-700 hover:border-[color:var(--gold)]/50 hover:-translate-y-0.5"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[color:var(--gold)]/40 text-gold transition-all duration-500 group-hover:border-[color:var(--gold)] group-hover:bg-[color:var(--gold)] group-hover:text-[#0a0a0a]">
                <Sparkles size={12} />
              </span>
              <h3 className="mt-5 font-display text-base font-bold uppercase tracking-[0.08em] text-ink">
                {v.title}
              </h3>
              <p className="mt-2 text-sm text-muted leading-relaxed">
                {v.text}
              </p>
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100"
                style={{
                  background:
                    "radial-gradient(circle at 30% 15%, rgba(201,169,97,0.14), transparent 55%)",
                }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
